// src/pages/notifications/AnalyticsPage.tsx
// Notification analytics: delivery stats, channel performance, daily trends.

import { useState, useEffect } from 'react';
import { observer } from '@react';
import { useNotificationsAnalytic, useI18n } from '@react/hooks';
import { Card, CardHeader, CardTitle, CardContent, ErrorAlert } from '@react/components';
import { StatusBadge } from '../../components/StatusBadge';
import { formatChannelType } from '../../features/notifications/logic';
import { NotificationIcon } from '../../components/NotificationIcon';
import type { GetNotificationStatsResponse } from '@core/types/notifications/GetNotificationStatsResponse';
import type { ChannelStats } from '@core/types/notifications/ChannelStats';
import type { DailyStats } from '@core/types/notifications/DailyStats';

export const AnalyticsPage = observer(() => {
  const store = useNotificationsAnalytic();
  const { t } = useI18n();
  const [stats, setStats] = useState<GetNotificationStatsResponse | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await store.GetNotificationStats();
        setStats(resp as GetNotificationStatsResponse);
      } catch {
        // handled by store
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const channelStats = (stats?.channel_stats as unknown as { items?: ChannelStats[] })?.items ?? [];
  const dailyStats = (stats?.daily_stats as unknown as { items?: DailyStats[] })?.items ?? [];

  const deliveryRate = stats && stats.total_sent > 0
    ? ((stats.total_delivered / stats.total_sent) * 100).toFixed(1)
    : '0';
  const readRate = stats && stats.total_delivered > 0
    ? ((stats.total_read / stats.total_delivered) * 100).toFixed(1)
    : '0';

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-foreground">{t('notifications.analytics')}</h2>

      {store.error ? <ErrorAlert error={store.error} /> : null}

      {store.isLoading ? (
        <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>
      ) : stats ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-foreground">{stats.total_sent}</p>
                <p className="text-sm text-muted-foreground">{t('notifications.totalSent')}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.total_delivered}</p>
                <p className="text-sm text-muted-foreground">{t('notifications.totalDelivered')}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.total_read}</p>
                <p className="text-sm text-muted-foreground">{t('notifications.totalRead')}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{stats.total_failed}</p>
                <p className="text-sm text-muted-foreground">{t('notifications.totalFailed')}</p>
              </CardContent>
            </Card>
          </div>

          {/* Rates */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-foreground">{deliveryRate}%</p>
                <p className="text-sm text-muted-foreground">{t('notifications.deliveryRate')}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-foreground">{readRate}%</p>
                <p className="text-sm text-muted-foreground">{t('notifications.readRate')}</p>
              </CardContent>
            </Card>
          </div>

          {/* Channel performance */}
          {channelStats.length > 0 && (
            <Card>
              <CardHeader><CardTitle>{t('notifications.channelPerformance')}</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {channelStats.map((cs, idx) => {
                    const ch = formatChannelType(cs.channel_type ?? '');
                    const rate = cs.sent > 0 ? ((cs.delivered / cs.sent) * 100).toFixed(1) : '0';
                    return (
                      <div key={idx} className="flex items-center gap-4 p-3 rounded-lg border border-border">
                        <NotificationIcon name={ch.icon} className="h-5 w-5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{t(ch.labelKey)}</p>
                          <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                            <span>{t('notifications.statSent')}: {cs.sent}</span>
                            <span>{t('notifications.statDelivered')}: {cs.delivered}</span>
                            <span>{t('notifications.statFailed')}: {cs.failed}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">{rate}%</p>
                          <p className="text-xs text-muted-foreground">{t('notifications.deliveryRate')}</p>
                        </div>
                        <StatusBadge
                          variant={parseFloat(rate) >= 95 ? 'success' : parseFloat(rate) >= 80 ? 'warning' : 'error'}
                          label={parseFloat(rate) >= 95 ? t('notifications.healthHealthy') : parseFloat(rate) >= 80 ? t('notifications.healthDegraded') : t('notifications.healthUnhealthy')}
                          size="sm"
                        />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Daily trends */}
          {dailyStats.length > 0 && (
            <Card>
              <CardHeader><CardTitle>{t('notifications.dailyTrends')}</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 font-medium text-muted-foreground">{t('notifications.date')}</th>
                        <th className="text-right py-2 font-medium text-muted-foreground">{t('notifications.statSent')}</th>
                        <th className="text-right py-2 font-medium text-muted-foreground">{t('notifications.statDelivered')}</th>
                        <th className="text-right py-2 font-medium text-muted-foreground">{t('notifications.statRead')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyStats.map((ds, idx) => (
                        <tr key={idx} className="border-b border-border last:border-0">
                          <td className="py-2">{ds.date}</td>
                          <td className="py-2 text-right">{ds.sent}</td>
                          <td className="py-2 text-right text-green-600">{ds.delivered}</td>
                          <td className="py-2 text-right text-blue-600">{ds.read}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">{t('common.noData')}</CardContent>
        </Card>
      )}
    </div>
  );
});

export default AnalyticsPage;
